import { useIntersection } from 'hooks/useIntersection';
import { useSWRPagination } from 'hooks/useSWRPagination';
import { flatMap } from 'lodash';
import React, { useEffect } from 'react';

export interface InfiniteScrollRender<T = any> extends Partial<ReturnType<typeof useSWRPagination>> {
    flattenedData: T[];
}

interface Props {
    swr?: Partial<ReturnType<typeof useSWRPagination>>;
    loader?: string | React.JSX.Element;
    endingIndicator?: string | React.JSX.Element;
    indicatorStyle?: React.CSSProperties;
    children: (data: InfiniteScrollRender) => React.JSX.Element;
}

export const InfiniteScroll = ({ swr, loader, endingIndicator, indicatorStyle, children }: Props) => {
    const { data, setSize, isValidating, isLoading, error, ...methods } = swr;
    const [intersecting, ref] = useIntersection<HTMLDivElement>();
    const ending = data?.[data.length - 1]?.next_page_url === null;
    const flattenedData = flatMap(data, (d) => d.data).filter((d) => d);

    useEffect(() => {
        if (intersecting && !isValidating && !ending && !error) {
            setSize((size) => size + 1);
        }
    }, [intersecting, isValidating, ending, error]);

    return (
        <>
            {children({
                data,
                flattenedData,
                ...methods
            })}
            <div style={{ ...indicatorStyle, position: 'relative' }}>
                <div ref={ref} style={{ position: 'absolute' }} />
                {ending ? (flattenedData.length > 0 ? endingIndicator : null) : loader}
            </div>
        </>
    );
};
